import styled, { css } from 'styled-components';
import { useParams, Link, Path } from 'react-router-dom';
import { ReactNode } from 'react';

import { Icon } from '@/components/Icon';
import {
  LearningCard,
  LearningCardError,
  LearningCardProps,
} from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCard';
import { PARENT_JOURNEY_ID_SEARCH_KEY } from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { JourneySectionCard } from '@/features/journey/admin/atoms/JourneySectionCard';
import { RLearningType } from '@/store/v2';

export type LearnersOrderedLearningCardProps = LearningCardProps & {
  launchPath?: string;
  onClick?: () => void;
  orderIndex: number;
};

const RootStyled = styled.div<{ $isSection: boolean }>`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 24px 16px 24px 32px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  margin-bottom: 24px;
  position: relative;
  background: white;
  z-index: 2;

  ${({ $isSection, theme }) =>
    $isSection &&
    css`
      background: ${theme.tones.neutral[99]};
    `};
`;

const SectionStackCard = styled.div<{ $index: number }>`
  z-index: 1;
  border: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  width: 100%;
  height: 100px;
  position: absolute;
  top: ${({ $index }) => `-${$index * 4}px`};
  left: 0;
  border-radius: 8px;
`;

const NavigateIcon = styled(Icon).attrs({ icon: 'angle-right' })`
  display: flex;
  width: 6px;
  margin: 0px 15px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const UnavailableIcon = styled(Icon).attrs({ icon: ['fas', 'circle-exclamation'] })`
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const OrderIndexStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.body4};
  color: ${({ theme }) => theme.colors.neutral.onContainer};
  background: ${({ theme }) => theme.colors.neutral.container};
  border-radius: 100px;
  height: 32px;
  width: 32px;
  min-width: 32px;
`;

const ContentStyled = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 0;
`;

const ContainerStyled = styled.div`
  z-index: 2;
  position: relative;
  background: white;

  &:hover > ${RootStyled}, &:hover > ${SectionStackCard} {
    background: rgb(242, 249, 247);
    opacity: 1;
    border: 1px solid ${({ theme }) => theme.colors.neutral.onBackground};
  }
`;

const LockedContainer = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;
        `
      : css`
          cursor: pointer;
        `}
`;

const LinkWrapper = ({
  children,
  linkTo,
  locked,
  disabled,
  onClick,
}: {
  children: ReactNode;
  linkTo: Partial<Path>;
  locked?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) => {
  if (onClick || locked)
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <LockedContainer onClick={onClick} role="button" tabIndex={0} disabled={disabled}>
        {children}
      </LockedContainer>
    );

  return (
    <Link to={linkTo} state={{ noScroll: linkTo?.pathname?.includes('noScroll') }}>
      {children}
    </Link>
  );
};

export const LearnersOrderedLearningCard = ({
  orderIndex,
  launchPath,
  onClick,
  ...rest
}: LearnersOrderedLearningCardProps): JSX.Element => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const isSection = rest?.type === RLearningType.Section;

  const linkTo = {
    pathname: launchPath,
    search: `${PARENT_JOURNEY_ID_SEARCH_KEY}=${journeyId}`,
  };

  return (
    <LinkWrapper linkTo={linkTo} locked={rest.locked} onClick={onClick} disabled={rest.disabled}>
      <ContainerStyled>
        <RootStyled $isSection={isSection}>
          <ContentStyled>
            <OrderIndexStyled>{orderIndex}</OrderIndexStyled>
            {isSection ? <JourneySectionCard {...rest} /> : <LearningCard {...rest} />}
          </ContentStyled>
          {rest.error === LearningCardError.UNAVAILABLE && <UnavailableIcon />}
          {!rest.error && launchPath && (!rest.locked || Boolean(onClick)) && <NavigateIcon />}
        </RootStyled>
        {isSection && (
          <>
            <SectionStackCard $index={3} />
            <SectionStackCard $index={2} />
            <SectionStackCard $index={1} />
          </>
        )}
      </ContainerStyled>
    </LinkWrapper>
  );
};
